import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Brain, Download, Copy, Check, X, ChevronRight, ChevronDown, File, Folder, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import MonacoEditor from '@monaco-editor/react';
import { copyProjectToClipboard, copyFullZipToClipboard, copySingleFileToClipboard } from '../utils/copyUtils';
import './EditorPage.css';

const getIcon = (ext) => {
  const map = { js:'🟨', jsx:'🟦', ts:'🔷', tsx:'🔷', py:'🐍', java:'☕', css:'🎨', html:'🌐', json:'📋', md:'📝', go:'🐹', rs:'🦀', php:'🐘' };
  return map[ext] || '📄';
};

const FileNode = ({ node, depth = 0, onSelect, selectedPath }) => {
  const [open, setOpen] = useState(depth < 2);
  if (!node) return null;
  const ext = node.name.split('.').pop();

  if (node.type === 'folder') {
    return (
      <div>
        <div className={`tree-node folder-node`} style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => setOpen(!open)}>
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {open ? <FolderOpen size={13} /> : <Folder size={13} />}
          <span>{node.name}</span>
        </div>
        {open && node.children?.map((child, i) => (
          <FileNode key={i} node={child} depth={depth + 1} onSelect={onSelect} selectedPath={selectedPath} />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`tree-node file-node ${selectedPath === node.path ? 'active' : ''}`}
      style={{ paddingLeft: `${depth * 16 + 8}px` }}
      onClick={() => onSelect(node)}
    >
      <span className="file-emoji">{getIcon(ext)}</span>
      <span>{node.name}</span>
    </div>
  );
};

export default function EditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFile, setActiveFile] = useState(null);
  const [openTabs, setOpenTabs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [aiPanel, setAiPanel] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchProject(); }, [projectId]);

  const fetchProject = async () => {
    try {
      const res = await api.get(`/files/projects/${projectId}`);
      setProject(res.data.project);
    } catch { toast.error('Project not found'); navigate('/dashboard'); }
    finally { setLoading(false); }
  };

  const openFile = (node) => {
    if (!node.content && node.type === 'file') return toast.error('Cannot open this file');
    setActiveFile(node);
    setOpenTabs(prev => {
      if (prev.find(t => t.path === node.path)) return prev;
      return [...prev, node];
    });
  };

  const closeTab = (e, path) => {
    e.stopPropagation();
    const next = openTabs.filter(t => t.path !== path);
    setOpenTabs(next);
    if (activeFile?.path === path) setActiveFile(next[next.length - 1] || null);
  };

  const copyCode = () => {
    if (!activeFile?.content) return;
    navigator.clipboard.writeText(activeFile.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied!');
  };

  const searchCode = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.post(`/files/projects/${projectId}/search`, { query: searchQuery });
      setSearchResults(res.data.results);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const explainWithAI = async () => {
    if (!activeFile?.content) return toast.error('Open a file first');
    setAiPanel(true);
    setAiLoading(true);
    setAiResult('');
    try {
      const res = await api.post('/ai/explain', { code: activeFile.content, filename: activeFile.name, projectId });
      setAiResult(res.data.explanation);
    } catch (err) {
      if (err.response?.data?.requiresUpgrade) {
        toast.error('Upgrade your plan for more AI requests');
        setAiPanel(false);
      } else {
        toast.error('AI analysis failed');
      }
    } finally { setAiLoading(false); }
  };

  const handleCopyProject = async () => {
    await copyProjectToClipboard(project);
  };

  const handleCopyFullZip = async () => {
    await copyFullZipToClipboard(project);
  };

  const handleCopySingleFile = async () => {
    await copySingleFileToClipboard(activeFile);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!project) return null;

  return (
    <div className="editor-page">
      {/* Top Bar */}
      <div className="editor-topbar">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} /> Dashboard
        </button>
        <span className="project-title">{project.name}</span>
        <div className="topbar-actions">
          <button className="topbar-btn copy-project-btn" onClick={handleCopyProject} title="Copy entire project structure (filters ignored files)">
            <Copy size={15} /> Copy Project
          </button>
          <button className="topbar-btn copy-zip-btn" onClick={handleCopyFullZip} title="Copy all files as one block">
            <Copy size={15} /> Copy ZIP
          </button>
          <button className={`topbar-btn ${showSearch ? 'active' : ''}`} onClick={() => setShowSearch(!showSearch)}>
            <Search size={15} /> Search
          </button>
          <button className="topbar-btn ai-btn" onClick={explainWithAI}>
            <Brain size={15} /> AI Explain
          </button>
        </div>
      </div>

      <div className="editor-layout">
        {/* Sidebar */}
        <div className="editor-sidebar">
          <div className="sidebar-header">
            <span>FILES</span>
            <span className="file-count">{project.totalFiles}</span>
          </div>

          {showSearch && (
            <div className="sidebar-search">
              <input className="input" placeholder="Search files & code..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchCode()} />
              <button className="btn btn-primary" style={{ padding: '8px', marginTop: '8px', width: '100%', justifyContent: 'center', fontSize: '12px' }} onClick={searchCode} disabled={searching}>
                {searching ? 'Searching...' : 'Search'}
              </button>
              {searchResults.length > 0 && (
                <div className="search-results-list">
                  {searchResults.map((r, i) => (
                    <div key={i} className="search-result-item"
                      onClick={() => { const node = findNode(project.fileTree, r.path); if (node) openFile(node); }}>
                      <span className="result-path">{r.path}</span>
                      {r.matches?.slice(0,2).map((m, j) => (
                        <span key={j} className="result-line">L{m.line}: {m.text.slice(0, 40)}</span>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="file-tree">
            {project.fileTree?.children?.map((node, i) => (
              <FileNode key={i} node={node} depth={0} onSelect={openFile} selectedPath={activeFile?.path} />
            ))}
          </div>
        </div>

        {/* Editor Main */}
        <div className="editor-main">
          {/* Tabs */}
          {openTabs.length > 0 && (
            <div className="editor-tabs">
              {openTabs.map(tab => (
                <div key={tab.path} className={`editor-tab ${activeFile?.path === tab.path ? 'active' : ''}`}
                  onClick={() => setActiveFile(tab)}>
                  <span>{tab.name}</span>
                  <button className="tab-close" onClick={e => closeTab(e, tab.path)}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}

          {/* Code Area */}
          {activeFile ? (
            <div className="code-area">
              <div className="code-toolbar">
                <span className="code-lang">{activeFile.language || 'plaintext'}</span>
                <div className="code-actions">
                  <button className="code-btn" onClick={handleCopySingleFile} title="Copy this file">
                    <Copy size={13} /> Copy File
                  </button>
                  <button className="code-btn" onClick={copyCode}>
                    {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy Code</>}
                  </button>
                </div>
              </div>
              <div className="monaco-wrapper">
                <MonacoEditor
                  height="calc(100vh - 145px)"
                  language={activeFile.language || 'plaintext'}
                  value={activeFile.content || ''}
                  theme="vs-dark"
                  options={{
                    readOnly: false, minimap: { enabled: true },
                    fontSize: 13, fontFamily: "'JetBrains Mono', monospace",
                    lineNumbers: 'on', wordWrap: 'on',
                    scrollBeyondLastLine: false, smoothScrolling: true,
                    cursorBlinking: 'smooth', renderLineHighlight: 'all',
                    padding: { top: 12 }
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="editor-empty">
              <div className="editor-empty-icon">📂</div>
              <h3>Select a file to view</h3>
              <p>Click any file in the tree to open it in the editor</p>
            </div>
          )}
        </div>

        {/* AI Panel */}
        {aiPanel && (
          <div className="ai-panel">
            <div className="ai-panel-header">
              <span><Brain size={16} /> AI Analysis</span>
              <button onClick={() => setAiPanel(false)}><X size={16} /></button>
            </div>
            <div className="ai-panel-body">
              {aiLoading ? (
                <div className="ai-loading">
                  <div className="ai-dots"><span/><span/><span/></div>
                  <p>Analyzing code...</p>
                </div>
              ) : (
                <pre className="ai-result">{aiResult}</pre>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Recursively build a formatted string of the entire project structure
 * Including folder hierarchy and file contents
 */
function findNode(tree, path) {
  if (!tree) return null;
  if (tree.path === path) return tree;
  if (tree.children) {
    for (const child of tree.children) {
      const found = findNode(child, path);
      if (found) return found;
    }
  }
  return null;
}
